import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  Link2,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Trash2,
  Download
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.asistentetradingia.com';

interface Credentials {
  tradovate_user: string;
  tradovate_password: string;
  tradovate_app_id: string;
  tradovate_app_version: string;
  tradovate_cid: string;
  tradovate_secret: string;
  use_demo: boolean;
}

interface ConnectionInfo {
  tradovate_user: string;
  tradovate_app_id: string;
  tradovate_app_version: string;
  tradovate_cid: string;
  use_demo: boolean;
  auto_execute: boolean;
  connection_status: string;
  last_verified_at: string | null;
}

const defaultCreds: Credentials = {
  tradovate_user: '',
  tradovate_password: '',
  tradovate_app_id: '',
  tradovate_app_version: '1.0',
  tradovate_cid: '',
  tradovate_secret: '',
  use_demo: true,
};

async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || '';
}

const BrokerConnection = () => {
  const [credentials, setCredentials] = useState<Credentials>(defaultCreds);
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'verifying' | 'deleting'>('idle');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/broker/credentials`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.tradovate_user) {
          setConnectionInfo(data);
          setCredentials(prev => ({
            ...prev,
            tradovate_user: data.tradovate_user || '',
            tradovate_app_id: data.tradovate_app_id || '',
            tradovate_app_version: data.tradovate_app_version || '1.0',
            tradovate_cid: data.tradovate_cid || '',
            use_demo: data.use_demo ?? true,
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching credentials:', err);
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (text: string, type: 'success' | 'error') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => { setMessage(''); setMessageType(''); }, 5000);
  };

  const handleSave = async () => {
    setStatus('saving');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/broker/credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(credentials),
      });
      if (res.ok) {
        showMsg('Credenciales guardadas', 'success');
        await fetchCredentials();
      } else {
        showMsg('Error al guardar', 'error');
      }
    } catch {
      showMsg('Error de conexión', 'error');
    } finally {
      setStatus('idle');
    }
  };

  const handleVerify = async () => {
    setStatus('verifying');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/broker/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await res.json();
      showMsg(data.message || data.status, data.status === 'connected' ? 'success' : 'error');
      await fetchCredentials();
    } catch {
      showMsg('Error verificando conexión', 'error');
    } finally {
      setStatus('idle');
    }
  };

  const handleAutoExecute = async (enabled: boolean) => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/broker/auto-execute`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ auto_execute: enabled }),
      });
      if (res.ok) {
        showMsg(enabled ? 'Ejecución automática activada' : 'Ejecución automática desactivada', 'success');
        await fetchCredentials();
      } else {
        const data = await res.json();
        showMsg(data.detail || 'Error', 'error');
      }
    } catch {
      showMsg('Error de conexión', 'error');
    }
  };

  const handleDelete = async () => {
    if (!confirm('¿Eliminar credenciales? Esta acción no se puede deshacer.')) return;
    setStatus('deleting');
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/api/broker/credentials`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setCredentials(defaultCreds);
      setConnectionInfo(null);
      showMsg('Credenciales eliminadas', 'success');
    } catch {
      showMsg('Error al eliminar', 'error');
    } finally {
      setStatus('idle');
    }
  };

  const connStatus = connectionInfo?.connection_status || 'disconnected';
  const hasCredentials = !!connectionInfo?.tradovate_user;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Link2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Cuenta de Broker</h1>
          <p className="text-sm text-gray-400">Conecta tu cuenta de Tradovate para ejecución automática</p>
        </div>
      </div>

      {/* Guía de creación de cuenta */}
      <div className="mb-6 flex items-center justify-between bg-white/5 border border-white/10 rounded-lg p-4">
        <div>
          <p className="text-sm text-white font-medium">¿No tienes cuenta en Tradovate?</p>
          <p className="text-xs text-gray-400 mt-0.5">Descarga la guía paso a paso para crear tu cuenta</p>
        </div>
        <a
          href="/Crear_cuenta_tradovate.pdf"
          download
          className="flex items-center space-x-2 px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg text-sm transition-colors"
        >
          <Download className="h-4 w-4" />
          <span>Descargar guía</span>
        </a>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg border ${
          messageType === 'success'
            ? 'bg-green-900/20 border-green-900/50 text-green-400'
            : 'bg-red-900/20 border-red-900/50 text-red-400'
        }`}>
          {message}
        </div>
      )}

      {/* SECCIÓN 1: Estado de conexión */}
      <div className="card mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {connStatus === 'connected' ? (
              <CheckCircle className="h-5 w-5 text-green-400" />
            ) : connStatus === 'error' ? (
              <XCircle className="h-5 w-5 text-red-400" />
            ) : (
              <div className="h-5 w-5 rounded-full bg-gray-600" />
            )}
            <div>
              <p className="text-white font-medium">
                {connStatus === 'connected' ? 'Conectado' :
                 connStatus === 'error' ? 'Error de conexión' :
                 'Sin conectar'}
              </p>
              {connectionInfo?.last_verified_at && (
                <p className="text-xs text-gray-500">
                  Verificado: {new Date(connectionInfo.last_verified_at).toLocaleString('es-ES')}
                </p>
              )}
            </div>
          </div>
          {connectionInfo?.use_demo === false && (
            <span className="text-xs bg-red-900/30 text-red-400 px-2 py-1 rounded">LIVE</span>
          )}
          {connectionInfo?.use_demo === true && hasCredentials && (
            <span className="text-xs bg-yellow-900/30 text-yellow-400 px-2 py-1 rounded">DEMO</span>
          )}
        </div>
      </div>

      {/* SECCIÓN 2: Formulario */}
      <div className="card mb-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">Credenciales API</h2>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Usuario Tradovate</label>
          <input
            type="text"
            className="input-field w-full"
            value={credentials.tradovate_user}
            onChange={e => setCredentials(p => ({ ...p, tradovate_user: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Contraseña</label>
          <div className="relative">
            <input
              type={showPasswords ? 'text' : 'password'}
              className="input-field w-full pr-10"
              value={credentials.tradovate_password}
              onChange={e => setCredentials(p => ({ ...p, tradovate_password: e.target.value }))}
              placeholder={hasCredentials ? '••••••••' : ''}
            />
            <button
              type="button"
              onClick={() => setShowPasswords(!showPasswords)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">App ID</label>
            <input
              type="text"
              className="input-field w-full"
              value={credentials.tradovate_app_id}
              onChange={e => setCredentials(p => ({ ...p, tradovate_app_id: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">App Version</label>
            <input
              type="text"
              className="input-field w-full"
              value={credentials.tradovate_app_version}
              onChange={e => setCredentials(p => ({ ...p, tradovate_app_version: e.target.value }))}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">CID</label>
          <input
            type="text"
            className="input-field w-full"
            value={credentials.tradovate_cid}
            onChange={e => setCredentials(p => ({ ...p, tradovate_cid: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Secret</label>
          <div className="relative">
            <input
              type={showPasswords ? 'text' : 'password'}
              className="input-field w-full pr-10"
              value={credentials.tradovate_secret}
              onChange={e => setCredentials(p => ({ ...p, tradovate_secret: e.target.value }))}
              placeholder={hasCredentials ? '••••••••' : ''}
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Entorno</label>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCredentials(p => ({ ...p, use_demo: true }))}
                className={`px-3 py-1.5 rounded text-sm ${
                  credentials.use_demo ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-400'
                }`}
              >
                Demo
              </button>
              <button
                onClick={() => setCredentials(p => ({ ...p, use_demo: false }))}
                className={`px-3 py-1.5 rounded text-sm ${
                  !credentials.use_demo ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-400'
                }`}
              >
                Live
              </button>
            </div>
          </div>
        </div>
        {!credentials.use_demo && (
          <div className="flex items-start space-x-2 bg-red-900/20 border border-red-900/50 rounded-lg p-3">
            <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-400">
              Usa Live solo cuando hayas verificado que todo funciona correctamente en Demo.
            </p>
          </div>
        )}

        <p className="text-xs text-gray-500 pt-2">
          Tus credenciales se cifran antes de guardarse.
          Nunca compartimos ni accedemos a tu cuenta sin tu autorización explícita.
        </p>
      </div>

      {/* SECCIÓN 3: Acciones */}
      <div className="card space-y-4">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleSave}
            disabled={status !== 'idle' || !credentials.tradovate_user}
            className="btn-primary flex items-center space-x-2"
          >
            {status === 'saving' && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{status === 'saving' ? 'Guardando...' : 'Guardar credenciales'}</span>
          </button>

          <button
            onClick={handleVerify}
            disabled={status !== 'idle' || !hasCredentials}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors flex items-center space-x-2"
          >
            {status === 'verifying' && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{status === 'verifying' ? 'Verificando...' : 'Verificar conexión'}</span>
          </button>

          {hasCredentials && (
            <button
              onClick={handleDelete}
              disabled={status !== 'idle'}
              className="px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg text-sm transition-colors flex items-center space-x-2"
            >
              <Trash2 className="h-4 w-4" />
              <span>Eliminar</span>
            </button>
          )}
        </div>

        {/* Auto-execute toggle — solo si conectado */}
        {connStatus === 'connected' && (
          <div className="pt-4 border-t border-white/5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-white">Ejecución automática</h3>
                <p className="text-xs text-gray-400 mt-1 max-w-md">
                  Al activar, el sistema ejecutará órdenes en tu cuenta de Tradovate
                  automáticamente cuando detecte un setup de alta calidad.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={connectionInfo?.auto_execute || false}
                  onChange={e => handleAutoExecute(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            {connectionInfo?.auto_execute && (
              <div className="mt-2 flex items-start space-x-2 bg-yellow-900/20 border border-yellow-900/50 rounded-lg p-3">
                <AlertTriangle className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-400">
                  Solo se ejecutan setups con 5+ confluencias. Revisa los resultados regularmente.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrokerConnection;
